import { useState } from 'react';
import { EventBus, GROUP_MAX_MEMBERS, GROUP_TITLE_MAX_LENGTH } from '@campus/core';
import { Badge, Button, Input, Menu, MenuItem } from '../ds/index.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { useMembersStore, sessionInfo } from '../people/membersStore.js';
import { MessagingApiError } from '../../messaging/client.js';
import {
  useMessagingStore, renameGroup, addGroupMembers, setGroupAdmin, removeGroupMember, leaveGroup, deleteGroup,
} from '../../messaging/store.js';
import { MemberMultiPicker } from './GroupCreator.js';
import { sendErrorLabel } from './format.js';

const fail = (err: unknown) => {
  const code = err instanceof MessagingApiError ? err.code : 'NETWORK';
  EventBus.emit('ui:toast', { message: sendErrorLabel(code).replace(/^Pas envoyé/, 'Pas enregistré'), tone: 'warning', duration: 6000 });
};

/**
 * Fiche d'un groupe : nom, membres, administrateurs ; ajouter, retirer,
 * nommer un admin, renommer (admins du groupe) ; quitter (tout le monde).
 */
export function GroupDetails({ conversationId, onBack, onLeft }: { conversationId: string; onBack: () => void; onLeft: () => void }) {
  const conv = useMessagingStore((s) => s.conversations.find((c) => c.id === conversationId));
  const members = useMembersStore((s) => s.members);
  const { userId: me } = sessionInfo();
  const [title, setTitle] = useState(conv?.title ?? '');
  const [adding, setAdding] = useState<string[] | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'remove'; userId: string } | { kind: 'leave' } | { kind: 'delete' } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!conv || conv.kind !== 'group') return null;
  const isAdmin = conv.adminIds.includes(me);
  const nameOf = (id: string) => members.get(id)?.displayName ?? 'Membre';
  // Administrateurs d'abord, puis dans l'ordre d'arrivée.
  const ordered = [...conv.memberIds].sort((a, b) => Number(conv.adminIds.includes(b)) - Number(conv.adminIds.includes(a)));

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (err) { fail(err); } finally { setBusy(false); }
  };

  if (adding) {
    const room = GROUP_MAX_MEMBERS - conv.memberIds.length;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button variant="ghost" size="sm" icon="arrow-left" aria-label="Retour à la fiche du groupe" onClick={() => setAdding(null)} />
          <h3 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 700 }}>Ajouter au groupe</h3>
        </div>
        <MemberMultiPicker selected={adding} exclude={conv.memberIds} max={room}
          onToggle={(id) => setAdding((list) => (list!.includes(id) ? list!.filter((x) => x !== id) : [...list!, id]))} />
        <div style={{ padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--color-border-default)' }}>
          <Button variant="primary" icon="user-plus" disabled={adding.length === 0 || busy} style={{ width: '100%' }}
            onClick={() => { void run(async () => { await addGroupMembers(conversationId, adding); setAdding(null); }); }}>
            {adding.length > 0 ? `Ajouter ${adding.length} personne${adding.length > 1 ? 's' : ''}` : 'Ajouter'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--color-border-default)' }}>
        <Button variant="ghost" size="sm" icon="arrow-left" aria-label="Retour à la conversation" onClick={onBack} />
        <h3 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 700 }}>Infos du groupe</h3>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {isAdmin ? (
          <form style={{ display: 'flex', gap: 8 }}
            onSubmit={(e) => { e.preventDefault(); if (title.trim() && title.trim() !== conv.title) void run(() => renameGroup(conversationId, title.trim())); }}>
            <Input aria-label="Nom du groupe" maxLength={GROUP_TITLE_MAX_LENGTH} value={title} onChange={(e) => setTitle(e.target.value)} className="pl-grow" />
            <Button variant="secondary" type="submit" disabled={busy || !title.trim() || title.trim() === conv.title}>Renommer</Button>
          </form>
        ) : (
          <p style={{ margin: 0, fontSize: 'var(--text-md)', fontWeight: 700 }}>{conv.title}</p>
        )}

        <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className="pl-menu-label" style={{ margin: 0 }}>{conv.memberIds.length} membre{conv.memberIds.length > 1 ? 's' : ''}</p>
            {isAdmin && conv.memberIds.length < GROUP_MAX_MEMBERS && (
              <Button size="sm" variant="ghost" icon="user-plus" onClick={() => setAdding([])}>Ajouter des personnes</Button>
            )}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {ordered.map((id) => {
              const admin = conv.adminIds.includes(id);
              const self = id === me;
              // Ses propres droits : seulement s'il reste un autre admin.
              const canManage = isAdmin && (!self || (admin && conv.adminIds.length > 1));
              return (
                <li key={id} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '4px 0' }}>
                  <PersonAvatar userId={id} name={nameOf(id)} size={32} highlight={self} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-sm)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {self ? `${nameOf(id)} (toi)` : nameOf(id)}
                  </span>
                  {admin && <Badge tone="primary">Admin</Badge>}
                  {canManage && (
                    <Button size="sm" variant="ghost" icon="settings" aria-label={`Gérer ${nameOf(id)}`} aria-expanded={menuFor === id}
                      onClick={() => setMenuFor((v) => (v === id ? null : id))} />
                  )}
                  {menuFor === id && (
                    <Menu label={`Gérer ${nameOf(id)}`} onClose={() => setMenuFor(null)} style={{ position: 'absolute', right: 0, top: 42 }}>
                      <MenuItem icon="shield" onSelect={() => { setMenuFor(null); void run(() => setGroupAdmin(conversationId, id, !admin)); }}>
                        {admin ? (self ? 'Ne plus être admin' : 'Retirer les droits d’admin') : 'Donner les droits d’admin'}
                      </MenuItem>
                      {!self && (
                        <MenuItem icon="log-out" danger onSelect={() => { setMenuFor(null); setConfirm({ kind: 'remove', userId: id }); }}>
                          Retirer du groupe
                        </MenuItem>
                      )}
                    </Menu>
                  )}
                </li>
              );
            })}
          </ul>
          {!isAdmin && <p className="pl-help" style={{ margin: 0 }}>Seuls les admins du groupe ajoutent ou retirent des personnes.</p>}
        </section>

        {confirm?.kind === 'remove' && (
          <div role="alertdialog" aria-label="Retirer du groupe" className="pl-card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>Retirer {nameOf(confirm.userId)} du groupe ? Cette personne ne verra plus les messages.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Annuler</Button>
              <Button size="sm" variant="danger" disabled={busy}
                onClick={() => { const { userId } = confirm; setConfirm(null); void run(() => removeGroupMember(conversationId, userId)); }}>Retirer</Button>
            </div>
          </div>
        )}
      </div>
      <div style={{ padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {confirm?.kind === 'leave' ? (
          <>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>Quitter « {conv.title} » ? Tu ne verras plus ses messages, sauf si un admin t’ajoute à nouveau.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Annuler</Button>
              <Button size="sm" variant="danger" disabled={busy}
                onClick={() => { void run(async () => { await leaveGroup(conversationId); onLeft(); }); }}>Quitter le groupe</Button>
            </div>
          </>
        ) : confirm?.kind === 'delete' ? (
          <>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>Supprimer « {conv.title} » pour tout le monde ? Les messages et les fichiers seront effacés, sans retour possible.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Annuler</Button>
              <Button size="sm" variant="danger" disabled={busy}
                onClick={() => { void run(async () => { await deleteGroup(conversationId); onLeft(); }); }}>Supprimer le groupe</Button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button variant="ghost" icon="log-out" onClick={() => setConfirm({ kind: 'leave' })} style={{ flex: '1 1 150px', color: 'var(--color-danger)' }}>
              Quitter le groupe
            </Button>
            {isAdmin && (
              <Button variant="ghost" icon="trash" onClick={() => setConfirm({ kind: 'delete' })} style={{ flex: '1 1 150px', color: 'var(--color-danger)' }}>
                Supprimer le groupe
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
